import { useState, useCallback, useMemo } from 'react';
import { Flex, Box, HStack, IconButton, Textarea, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { SendHorizonal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import ResizeTextarea from 'react-textarea-autosize';

import { TicketList, TicketsPageHeader, TicketHeader } from '@duke-hq/svc-support-web';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { CHAT_MESSAGE_MAX_LENGTH } from '@duke-hq/svc-support-web/config';

import { sizeVars } from '@duke-hq/ui/constants/layout';
import { StudentSelection } from '../students-page/StudentSelection';
import { ChatTypeSelector } from './ChatTypeSelector';

type StudentOption = RouterOutputs['users']['studentById'] & {
  label: string;
};

type ChatType = 'duke-support' | 'participant';

type Inputs = {
  message: string;
};

export function NewTicketPage() {
  const navigate = useNavigate();

  const [chatType, setChatType] = useState<ChatType>('duke-support');
  const [selectedParticipant, setSelectedParticipant] = useState<StudentOption | null>(null);

  const { register, handleSubmit, watch, setFocus } = useForm<Inputs>({});
  const draftMessage = watch('message', '');

  const createTicketMutation = trpcTenant.support.createTicketWithFirstMessage.useMutation();

  const { data: ticketListData, isLoading } = trpcTenant.support.listTickets.useQuery({
    limit: 50,
  });

  const isChatInputDisabled = chatType === 'participant' && selectedParticipant === null;

  const handleParticipantSelected = useCallback(
    (participant: StudentOption) => {
      setSelectedParticipant(participant);
      setFocus('message');
    },
    [setFocus],
  );

  // Placeholder ticket for new chat being composed
  const placeholderTicket = useMemo(
    () => ({
      id: 'new-chat-placeholder',
      subject: draftMessage ? `New chat | ${draftMessage.slice(0, 50)}` : 'New chat',
      requesterName:
        chatType === 'duke-support'
          ? 'Duke Support'
          : (selectedParticipant?.label ?? 'Participant'),
      requesterType: selectedParticipant?.label !== undefined ? 'Participant' : '',
      relativeTime: 'Just now',
      hasUnread: false,
    }),
    [draftMessage, chatType, selectedParticipant],
  );

  const tabs = [
    {
      key: 'open',
      label: <Trans>Open</Trans>,
      count: ticketListData?.openCount ?? 0,
      isActive: true,
      onClick: () => navigate('/inbox'),
    },
    {
      key: 'archived',
      label: <Trans>Archived</Trans>,
      isActive: false,
      onClick: () => navigate('/inbox?tab=archived'),
    },
  ];

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (data) => {
      if (data.message?.trim() === '' || isChatInputDisabled) {
        return;
      }

      const result = await createTicketMutation.mutateAsync({
        participantId: selectedParticipant !== null ? selectedParticipant.id : undefined,
        message: data.message.trim(),
      });

      navigate(`/inbox/${result.ticketId}`);
    },
    [selectedParticipant, isChatInputDisabled, createTicketMutation, navigate],
  );

  const onKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && e.shiftKey === false) {
        e.preventDefault();
        await handleSubmit(onSubmit)();
      }
    },
    [handleSubmit, onSubmit],
  );

  return (
    <Flex direction="column" w="100%" h={`calc(100vh - ${sizeVars.navbar})`}>
      <TicketsPageHeader
        actions={
          <Flex flex={1} justify="flex-end" align="center" gap={3}>
            <Text fontWeight="medium" color="fg.default" fontSize="sm">
              <Trans>Chat with</Trans>
            </Text>
            <ChatTypeSelector value={chatType} onChange={setChatType} />
          </Flex>
        }
      />

      <Flex flex={1} overflow="hidden">
        <TicketList
          loading={isLoading}
          tabs={tabs}
          tickets={[placeholderTicket, ...(ticketListData?.tickets ?? [])]}
          baseUrl="/inbox"
          selectedTicketId="new-chat-placeholder"
        />

        <Flex direction="column" flex={1} overflow="hidden" bg="bg.surface">
          <TicketHeader
            requesterName={placeholderTicket.requesterName}
            requesterType={placeholderTicket.requesterType}
            requesterEmail={selectedParticipant?.email ?? ''}
            actions={
              chatType === 'participant' ? (
                <Box width="300px">
                  <StudentSelection
                    student={selectedParticipant}
                    onStudentSelected={handleParticipantSelected}
                    disabledSelection={false}
                    size="sm"
                  />
                </Box>
              ) : undefined
            }
          />

          <Flex
            flex={1}
            direction="column"
            alignItems="center"
            justifyContent="center"
            p={8}
            color="fg.muted"
          >
            <Text textAlign="center">
              <Trans>Start a new conversation by typing your message below</Trans>
            </Text>
          </Flex>

          <Box
            bg="bg.subtle"
            borderTop="1px solid"
            borderColor="border.default"
            as="form"
            alignSelf="stretch"
            onSubmit={handleSubmit(onSubmit)}
          >
            <HStack p="8px" width="100%" alignItems="center">
              <Textarea
                as={ResizeTextarea}
                minH="unset"
                overflow="hidden"
                w="100%"
                resize="none"
                minRows={1}
                maxRows={3}
                placeholder="Send message…"
                autoFocus
                onKeyDown={onKeyDown}
                maxLength={CHAT_MESSAGE_MAX_LENGTH}
                disabled={isChatInputDisabled}
                {...register('message', {
                  required: true,
                })}
              />
              <IconButton
                aria-label="support-send-message-button"
                variant="tertiary"
                onClick={handleSubmit(onSubmit)}
                size="sm"
                isLoading={createTicketMutation.isPending}
                isDisabled={isChatInputDisabled || !draftMessage?.trim()}
              >
                <SendHorizonal strokeWidth={1.5} />
              </IconButton>
            </HStack>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
}
